import type { GroupType } from '@lms/api-client/backend';
import { intlLocale } from '@lms/charts';
import { Button } from '@lms/ui/button';
import { Input } from '@lms/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@lms/ui/table';
import { SearchIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLocale, useTranslations } from 'use-intl';
import type { GroupCompletions as GroupCompletionsRow } from '@/features/dashboard/coverage';

/** Rows shown before "Show all". */
const PREVIEW = 10;

// Same shades as the tenant/supplier segments in the per-curriculum card.
const TYPE_DOT: Record<GroupType, string> = {
  tenant: 'bg-primary',
  supplier: 'bg-primary/50',
};

/**
 * Every group with its latest completion in the period per curriculum: a
 * date, "–" when an expected curriculum has none, blank when the group isn't
 * expected to take it. Groups missing something first. Shows the first ten
 * with a "Show all" expansion and a search, since a center can have hundreds
 * of groups.
 */
export function GroupCompletions({
  rows,
  curricula,
  groupHref,
  types,
}: {
  rows: readonly GroupCompletionsRow[];
  /** One column each, in the order of the rows' cells. */
  curricula: readonly { id: number; name: string }[];
  groupHref: (groupId: number) => string;
  /** Adds a group-type column, for lists mixing tenants and suppliers. */
  types?: { heading: string; labels: Record<GroupType, string> };
}) {
  const t = useTranslations('stats');
  const t_admin = useTranslations('admin');
  const locale = intlLocale(useLocale());
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase();
    return q
      ? rows.filter((r) => r.group.name.toLocaleLowerCase().includes(q))
      : rows;
  }, [rows, query]);
  const visible = expanded || query ? filtered : filtered.slice(0, PREVIEW);

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end">
        <div className="relative w-full sm:w-64">
          <SearchIcon className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t_admin('search')}
            className="h-10 pl-8"
          />
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="text-muted-foreground rounded-md border border-dashed py-8 text-center text-sm">
          {t('no_data')}
        </p>
      ) : filtered.length === 0 ? (
        <p className="text-muted-foreground rounded-md border border-dashed py-8 text-center text-sm">
          {t_admin('no_matches')}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t_admin('name')}</TableHead>
              {types && <TableHead>{types.heading}</TableHead>}
              {curricula.map((c) => (
                <TableHead key={c.id} className="text-right">
                  {c.name}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map(({ group, cells }) => (
              <TableRow key={group.id}>
                <TableCell className="font-medium">
                  <Link to={groupHref(group.id)} className="hover:underline">
                    {group.name}
                  </Link>
                </TableCell>
                {types && (
                  <TableCell className="text-muted-foreground">
                    <span className="flex items-center gap-1.5">
                      <span
                        aria-hidden
                        className={`size-2 rounded-full ${TYPE_DOT[group.groupType]}`}
                      />
                      {types.labels[group.groupType]}
                    </span>
                  </TableCell>
                )}
                {cells.map((cell, i) => (
                  <TableCell
                    key={curricula[i].id}
                    className="text-muted-foreground text-right whitespace-nowrap tabular-nums"
                  >
                    {cell.last
                      ? formatDate(cell.last)
                      : cell.expected
                        ? '–'
                        : ''}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {!query && filtered.length > PREVIEW && (
        <Button
          variant="ghost"
          size="sm"
          className="self-center"
          onClick={() => setExpanded((e) => !e)}
        >
          {expanded
            ? t('show_fewer')
            : t('show_all', { count: filtered.length })}
        </Button>
      )}
    </div>
  );
}
